import { FormControl, Textarea } from "@primer/react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { lichSuXuongPhongYTeApi } from "../../api/lichSuXuongPhongYTeApi";
import { hocSinhLichSuYTeTypeApi } from "../../api/hocSinhLichSuYTeTypeApi";

import Button from "../../components/ui/button";
import Modal from "../../components/ui/modal";
import ModalActions from "../../components/ui/modal/ModalActions";
import TextInput from "../../components/ui/text-input";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ILichSuYteRequest } from "../../models/request/hoc-sinh/ILichSuYteRequest";
import SelectBoxHocSinh from "../../components/selectbox-hocsinh";
import ComboboxLichSuYTeType from "../../components/combobox-lich-su-y-te-type";
import MultiImageUpload from "../../components/multi-image-upload";
import SelectBoxHocSinhNew from "../../components/selectbox-hocsinh-new";

interface ILichSuYTeModalProps {
  id: number;
  onClose: () => void;
  onSuccess: () => void;
  prefilledData?: {
    ts_hocsinh_id: number;
    ho_ten: string;
    ma_hs: string;
    ten_lop: string;
    ten_khoi: string;
    ten_he: string;
    danThuocInfo?: any;
  };
}
const LichSuXuongPhongYTeModal = (props: ILichSuYTeModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id, dm_truong_id, dm_he_id } = useCommonSelectedHook();
  const [hocSinh, setHocSinh] = useState<any>(null);

  // Filter variables for SelectBoxHocSinhNew
  const filterTruongId = dm_truong_id;
  const filterKhoiId = 0; // Set to 0 or add state if needed
  const filterHeId = dm_he_id;

  const [formData, setFormData] = useState<ILichSuYteRequest>({
    ts_hocsinh_id: 0,
    ts_hocsinh_lichsu_yte_type_id: 0,
    fa_danthuoc_id: 0,
    tinh_trang: "",
    ket_qua: "",
    images: [],
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setError,
    control,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<ILichSuYteRequest>({
    mode: "onChange", // Validate khi người dùng thay đổi input
    reValidateMode: "onChange", // Re-validate khi có thay đổi
    defaultValues: formData,
  });

  useEffect(() => {
    if (props.id > 0) {
      handleGetVm();
    } else if (props.prefilledData) {
      // Nếu có prefilledData, tìm hoặc tạo loại "Dặn thuốc"
      handleGetOrCreateDanThuocType();
    }
  }, [props.id, props.prefilledData]);

  const handleGetOrCreateDanThuocType = async () => {
    try {
      // Lấy danh sách loại lịch sử y tế
      const res = await hocSinhLichSuYTeTypeApi.selectAll();
      if (res.is_success) {
        let danThuocTypeId = 0;
        const danThuocType = res.data.find((type: any) => type.nhom_noi_dung === "Dặn thuốc");

        if (danThuocType) {
          danThuocTypeId = danThuocType.id;
        } else {
          // Tạo mới loại "Dặn thuốc" nếu chưa có
          const createRes = await hocSinhLichSuYTeTypeApi.insert({
            id: 0,
            nhom_noi_dung: "Dặn thuốc",
            nhom_noi_dung_en: "Medicine Administration"
          });
          if (createRes.is_success) {
            danThuocTypeId = createRes.data.id;
          }
        }

        // Set dữ liệu mặc định
        const defaultData: ILichSuYteRequest = {
          ts_hocsinh_id: props.prefilledData!.ts_hocsinh_id,
          ts_hocsinh_lichsu_yte_type_id: danThuocTypeId,
          fa_danthuoc_id: props.prefilledData!.danThuocInfo?.id || 0, // Lưu ID của dặn thuốc
          tinh_trang: `Xác nhận cho uống thuốc ${new Date().toLocaleDateString('vi-VN')}`,
          ket_qua: props.prefilledData!.danThuocInfo?.chiTiets?.map((ct: any) => `${ct.ten_thuoc}: ${ct.cachdung_lieuluong}`).join('\n') || '',
          images: [],
        };
        setFormData(defaultData);
        reset(defaultData);
      }
    } catch (error) {
      console.error("Error getting or creating dan thuoc type:", error);
    }
  };

  const handleGetVm = async () => {
    const res = await lichSuXuongPhongYTeApi.selectById(props.id);
    if (res.is_success) {
      const responseData = res.data;
      const data: ILichSuYteRequest = {
        id: props.id,
        ts_hocsinh_id: responseData.lichSuYTe?.ts_hocsinh_id || responseData.ts_hocsinh_id,
        ts_hocsinh_lichsu_yte_type_id: responseData.lichSuYTe?.ts_hocsinh_lichsu_yte_type_id || responseData.ts_hocsinh_lichsu_yte_type_id,
        fa_danthuoc_id: responseData.lichSuYTe?.fa_danthuoc_id || responseData.fa_danthuoc_id || 0,
        tinh_trang: responseData.lichSuYTe?.tinh_trang || responseData.tinh_trang,
        ket_qua: responseData.lichSuYTe?.ket_qua || responseData.ket_qua,
        images: responseData.images || [],
      };
      setFormData(data);
      reset(data);
    } else {
      NotifyHelper.Error(res.message ?? "Error");
    }
  };

  const onSubmit = async (data: any) => {
    // Validate toàn bộ form trước khi submit
    const validationErrors: any = {};

    // Validate học sinh
    if (!data.ts_hocsinh_id || data.ts_hocsinh_id === 0) {
      validationErrors.ts_hocsinh_id = "Vui lòng chọn học sinh";
    }
    // Validate loại lịch sử y tế
    if (!data.ts_hocsinh_lichsu_yte_type_id || data.ts_hocsinh_lichsu_yte_type_id === 0) {
      validationErrors.ts_hocsinh_lichsu_yte_type_id = "Vui lòng chọn loại lịch sử y tế";
    }
    // Validate tình trạng
    if (!data.tinh_trang || data.tinh_trang.trim() === "") {
      validationErrors.tinh_trang = "Vui lòng điền tình trạng";
    }

    // Nếu có lỗi validation, hiển thị lỗi và dừng submit
    if (Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach(field => {
        setError(field as any, {
          type: "manual",
          message: validationErrors[field]
        });
      });
      NotifyHelper.Error("Vui lòng kiểm tra lại thông tin đã nhập");
      return;
    }

    setIsSaving(true);
    try {
      let res: any;
      if (props.id > 0) {
        res = await lichSuXuongPhongYTeApi.update({
          ...data,
          id: props.id,
        });
      } else {
        res = await lichSuXuongPhongYTeApi.insert({
          ...data
        });
      }

      if (res.is_success) {
        NotifyHelper.Success(props.id > 0 ? "Cập nhật thành công" : "Thêm mới thành công");
        props.onSuccess();
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi lưu dữ liệu");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi lưu dữ liệu");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={props.onClose}
      // width={"60%"}
      sx={{
        width: "60%"
      }}
      title={props.id > 0 ? "Cập nhật" : "Thêm mới"}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormControl>
              <FormControl.Label>Học sinh</FormControl.Label>
              <Controller
                name="ts_hocsinh_id"
                control={control}
                rules={{
                  required: "Vui lòng chọn học sinh",
                  validate: (value) => {
                    if (!value || value === 0) {
                      return "Vui lòng chọn học sinh";
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  props.prefilledData ? (
                    <TextInput
                      value={`${props.prefilledData.ma_hs} - ${props.prefilledData.ho_ten} (${props.prefilledData.ten_lop})`}
                      block
                      disabled
                      sx={{ backgroundColor: "canvas.subtle" }}
                    />
                  ) : (
                    <SelectBoxHocSinhNew
                      value={field.value}
                      onValueChanged={(id: number, data: any) => {
                        field.onChange(id);
                        setHocSinh(data);
                        clearErrors("ts_hocsinh_id");
                      }}
                      isShowClearBtn={true}
                      filterTruongId={filterTruongId}
                      filterKhoiId={filterKhoiId}
                      filterHeId={filterHeId}
                    />
                  )
                )}
              />
              {errors.ts_hocsinh_id && (
                <FormControl.Validation variant="error">
                  {errors.ts_hocsinh_id.message}
                </FormControl.Validation>
              )}
            </FormControl>
          </div>
          <div className="col-md-6 mb-3">
            <FormControl>
              <FormControl.Label>Loại lịch sử y tế</FormControl.Label>
              <Controller
                name="ts_hocsinh_lichsu_yte_type_id"
                control={control}
                rules={{
                  required: "Vui lòng chọn loại lịch sử y tế",
                  validate: (value) => {
                    if (!value || value === 0) {
                      return "Vui lòng chọn loại lịch sử y tế";
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  props.prefilledData ? (
                    <TextInput
                      value="Dặn thuốc"
                      block
                      disabled
                      sx={{ backgroundColor: "canvas.subtle" }}
                    />
                  ) : (
                    <ComboboxLichSuYTeType
                      value={field.value}
                      onValueChanged={(value) => {
                        field.onChange(value);
                        clearErrors("ts_hocsinh_lichsu_yte_type_id");
                      }}
                    />
                  )
                )}
              />
              {errors.ts_hocsinh_lichsu_yte_type_id && (
                <FormControl.Validation variant="error">
                  {errors.ts_hocsinh_lichsu_yte_type_id.message}
                </FormControl.Validation>
              )}
            </FormControl>
          </div>
          <div className="col-md-12 mb-3">
            <FormControl>
              <FormControl.Label>Tình trạng</FormControl.Label>
              <Controller
                name="tinh_trang"
                control={control}
                rules={{
                  required: "Vui lòng nhập tình trạng",
                  validate: (value) => {
                    if (!value) {
                      return "Vui lòng nhập tình trạng";
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  <TextInput
                    block
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      clearErrors("tinh_trang");
                    }}
                    placeholder="Nhập tình trạng"
                  />
                )}
              />
              {errors.tinh_trang && (
                <FormControl.Validation variant="error">
                  {errors.tinh_trang.message}
                </FormControl.Validation>
              )}
            </FormControl>
          </div>
          <div className="col-md-12 mb-3">
            <FormControl>
              <FormControl.Label>Kết quả</FormControl.Label>
              <Controller
                name="ket_qua"
                control={control}
                rules={{

                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={4}
                    block
                    placeholder="Nhập kết quả..."
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  />
                )}
              />
              {errors.ket_qua && (
                <FormControl.Validation variant="error">
                  {errors.ket_qua.message}
                </FormControl.Validation>
              )}
            </FormControl>
          </div>
          <div className="col-md-12 mb-3">
            <FormControl>
              <FormControl.Label>Hình ảnh</FormControl.Label>
              <Controller
                name="images"
                control={control}
                render={({ field }) => (
                  <MultiImageUpload
                    value={field.value || []}
                    onValueChanged={(images) => {
                      field.onChange(images);
                    }}
                    maxImages={10}
                    lichSuYTeId={props.id > 0 ? props.id : undefined}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>

        <ModalActions>
          <Button text="Base.Label.Close" onClick={props.onClose} />
          <Button
            text="Base.Label.Save"
            variant="primary"
            type="submit"
            isLoading={isSaving}
            disabled={isSaving}
          />
        </ModalActions>
      </form>
    </Modal>
  );
};

export default LichSuXuongPhongYTeModal;
